import ApiService from "./ApiService";
import Logger from "./Logger";

/** Same app codes as mobile publish batches so the PWA gets the same title/body. */
const WEB_PUSH_APP_CODES = ["jigsaw_muse", "sliding_puzzle_quest", "word_wheel_quest"];

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) {
        output[i] = raw.charCodeAt(i);
    }
    return output;
}

function canonicalDeviceToken(subscription) {
    const json = typeof subscription?.toJSON === "function" ? subscription.toJSON() : subscription;
    if (!json?.endpoint || !json?.keys?.p256dh || !json?.keys?.auth) {
        return null;
    }
    return JSON.stringify({
        endpoint: json.endpoint,
        keys: {
            p256dh: json.keys.p256dh,
            auth: json.keys.auth,
        },
    });
}

export async function ensurePuzzleServiceWorker() {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
        return null;
    }
    const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    return navigator.serviceWorker.register(`${base}/sw.js`);
}

function pushSupported() {
    return (
        typeof window !== "undefined" &&
        "Notification" in window &&
        "serviceWorker" in navigator &&
        "PushManager" in window
    );
}

async function registerTokenForApps(deviceToken) {
    for (const appCode of WEB_PUSH_APP_CODES) {
        try {
            await ApiService.postData("/home/push/device-token", {
                appCode,
                platform: "web",
                deviceToken,
            });
        } catch (error) {
            Logger.error("[WebPush] register failed", appCode, error);
        }
    }
}

async function unregisterTokenForApps(deviceToken) {
    if (!deviceToken) {
        return;
    }
    for (const appCode of WEB_PUSH_APP_CODES) {
        try {
            await ApiService.deleteData("/home/push/device-token", {
                appCode,
                deviceToken,
            });
        } catch (error) {
            Logger.error("[WebPush] unregister failed", appCode, error);
        }
    }
}

async function currentSubscription() {
    const registration = await ensurePuzzleServiceWorker();
    if (!registration?.pushManager) {
        return null;
    }
    return registration.pushManager.getSubscription();
}

/**
 * Keep the installed PWA subscribed to the same publish notifications as the mobile apps.
 * @param {boolean} enabled push preference (from notificationsByApp / settings toggle; default true)
 * @param {{ requestPermission?: boolean }} options request the browser prompt only from a user gesture
 */
async function syncFromPreference(enabled, options = {}) {
    if (!pushSupported()) {
        return { ok: false, reason: "unsupported" };
    }
    const requestPermission = options.requestPermission === true;

    if (!enabled) {
        try {
            const existing = await currentSubscription();
            const token = canonicalDeviceToken(existing);
            await unregisterTokenForApps(token);
            if (existing) {
                await existing.unsubscribe();
            }
        } catch (error) {
            Logger.error("[WebPush] unsubscribe failed", error);
        }
        return { ok: true, reason: "disabled" };
    }

    if (Notification.permission === "denied") {
        return { ok: false, reason: "denied" };
    }
    if (Notification.permission === "default") {
        if (!requestPermission) {
            return { ok: false, reason: "permission-default" };
        }
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
            return { ok: false, reason: permission };
        }
    }

    try {
        const config = await ApiService.getData("/home/push/web-config");
        const vapidPublicKey = config?.vapidPublicKey;
        if (!vapidPublicKey) {
            return { ok: false, reason: "no-vapid" };
        }
        const registration = await ensurePuzzleServiceWorker();
        if (!registration?.pushManager) {
            return { ok: false, reason: "no-push-manager" };
        }
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });
        }
        const deviceToken = canonicalDeviceToken(subscription);
        if (!deviceToken) {
            return { ok: false, reason: "invalid-subscription" };
        }
        await registerTokenForApps(deviceToken);
        return { ok: true, reason: "subscribed" };
    } catch (error) {
        Logger.error("[WebPush] subscribe failed", error);
        return { ok: false, reason: "error" };
    }
}

const WebPushService = {
    syncFromPreference,
    ensurePuzzleServiceWorker,
};

export default WebPushService;
